import { useState } from "react";
import Modal from "@/components/Modal";
import { Button } from "@/components/ui/button";
import LabelHint from "@/components/ui/LabelHint";
import { IngredientPackaging } from "@/redux/services/inventory/inventory.type";
import { useCrudIngredientPackaging } from "@/hooks/inventory/useCrudIngredientPackaging";

interface PackagingFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  ingredientUuid: string;
  baseUnit: string;
  packagingToEdit?: IngredientPackaging;
}

const PACKAGING_SUGGESTIONS = [
  "sac",
  "carton",
  "bouteille",
  "boîte",
  "paquet",
  "canette",
  "rouleau",
  "pot",
  "barquette",
  "bocal",
  "bidon",
  "caisse",
  "brique",
];

const PackagingFormModal = ({
  isOpen,
  onClose,
  ingredientUuid,
  baseUnit,
  packagingToEdit,
}: PackagingFormModalProps) => {
  const isEditMode = !!packagingToEdit;
  const { register, errors, setValue, watch, onSubmit, isLoading } =
    useCrudIngredientPackaging(onClose, ingredientUuid, packagingToEdit);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const nameValue = watch("name") ?? "";
  const filteredSuggestions = PACKAGING_SUGGESTIONS.filter((s) =>
    s.toLowerCase().includes(nameValue.toLowerCase())
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      disableAutoFocus
      title={
        isEditMode ? "Modifier le conditionnement" : "Ajouter un conditionnement"
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="relative">
          <label
            htmlFor="packaging-name"
            className="block text-sm font-medium text-card-foreground mb-1"
          >
            Nom
            <LabelHint text="Nom du conditionnement (ex: sac, carton, bouteille). Doit être unique pour cet ingrédient." />
          </label>
          <input
            id="packaging-name"
            type="text"
            {...register("name")}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="Ex: sac, carton, bouteille"
          />
          {showSuggestions && filteredSuggestions.length > 0 && (
            <ul className="absolute z-10 w-full bg-card border border-border rounded-md shadow-lg mt-1 max-h-40 overflow-y-auto">
              {filteredSuggestions.map((suggestion) => (
                <li key={suggestion}>
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-muted"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setValue("name", suggestion, { shouldValidate: true });
                      setShowSuggestions(false);
                    }}
                  >
                    {suggestion}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {errors.name && (
            <p className="mt-1 text-xs text-danger">{errors.name.message}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="packaging-quantity"
            className="block text-sm font-medium text-card-foreground mb-1"
          >
            Quantité par conditionnement
            <span className="text-muted-foreground font-normal"> ({baseUnit})</span>
            <LabelHint text={`Nombre d'unités de base contenues dans un conditionnement. Ex: 1 sac de riz = 25 ${baseUnit} → saisir 25.`} />
          </label>
          <input
            id="packaging-quantity"
            type="text"
            inputMode="decimal"
            {...register("quantityInBase")}
            className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="Ex: 25"
          />
          <p className="mt-1 text-[11px] text-muted-foreground">
            1 conditionnement = X {baseUnit}
          </p>
          {errors.quantityInBase && (
            <p className="mt-1 text-xs text-danger">
              {errors.quantityInBase.message}
            </p>
          )}
        </div>

        <div>
          <label className="flex items-center gap-2 text-sm text-card-foreground">
            <input
              type="checkbox"
              {...register("isDefault")}
              className="h-4 w-4 rounded border-border accent-primary"
            />
            Utiliser comme conditionnement par défaut
          </label>
          <p className="mt-1 text-[11px] text-muted-foreground">
            Pré-sélectionné lors de la création de bons de commande.
          </p>
        </div>

        <div className="flex justify-end pt-2">
          <Button type="submit" isLoading={isLoading} size="sm">
            Enregistrer
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default PackagingFormModal;
